import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { gql } from 'graphql-tag';
import { DashboardTable } from 'src/components/DashboardTable';
import {
  CREATE_CUSTOM_REPORT,
  UPDATE_CUSTOM_REPORT,
} from 'src/utils/gql/mutations';
import { GET_CUSTOM_REPORTS } from 'src/utils/gql/queries';
import { CustomReport as CustomReportType } from 'src/utils/gql/types';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { KIPIFY_FILTER_APPLY_BUTTON_CLASS } from 'src/theme/kipifyUi';
import { useNavigate, useLocation } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import useBackendConnectors from 'src/hooks/useBackendConnectors';
import { getTitle } from 'src/components/DashboardTable/utils';
import {
  formatDateForDisplay,
  getPreviousPeriodDateRange,
  getPreviousYearDateRange,
} from 'src/utils/dateUtils';
import { ConnectorType, MetricsByConnectorType } from 'src/utils/gql/types';
import { FormControl, MenuItem, Select } from '@mui/material';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import {
  getLookbackCompareAutoDisabledMessage,
  getLookbackCompareBlockedMessage,
  getLookbackCustomRangeBlockedMessage,
  getLookbackPlanLimitCaption,
} from 'src/utils/lookbackMessages';

/**
 * Filtra i connettori non validi da metricValuesRowsGroupedByConnector
 * per GraphQL (rimuove kipify, kipify_normalized e altri campi non definiti nello schema)
 */
function filterValidGraphQLConnectorsFromConfig(config: any): any {
  if (!config || typeof config !== 'object') {
    return config;
  }

  // Se c'è metricValuesRowsGroupedByConnector, filtra i connettori non validi
  if (
    config.metricValuesRowsGroupedByConnector &&
    typeof config.metricValuesRowsGroupedByConnector === 'object'
  ) {
    const validConnectors = [
      ConnectorType.SHOPIFY,
      ConnectorType.MAGENTO,
      ConnectorType.FACEBOOK,
      ConnectorType.GOOGLE_ADS,
      ConnectorType.GOOGlE_ANALYTICS4,
      ConnectorType.GOOGLE_SEARCH_CONSOLE,
      ConnectorType.DATABASE,
      MetricsByConnectorType.CUSTOM_FORMULA,
    ];

    const filtered: Record<string, any> = {};
    Object.entries(config.metricValuesRowsGroupedByConnector).forEach(
      ([key, value]) => {
        // Filtra anche varianti di kipify
        if (
          key === ConnectorType.KIPIFY ||
          key === MetricsByConnectorType.KIPIFY ||
          key === 'kipify' ||
          key === 'kipify_normalized'
        ) {
          return;
        }
        if (validConnectors.includes(key as any)) {
          filtered[key] = value;
        }
      },
    );

    return {
      ...config,
      metricValuesRowsGroupedByConnector: filtered,
    };
  }

  return config;
}
// Container responsive ottimizzato per tutti i dispositivi
const CustomReportContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible', // Cambiato da 'hidden' a 'visible' per evitare tagli

  // Mantiene il contenuto fuori dalla sidebar senza usare 100vw, che taglia il lato destro.
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    padding: 0, // Rimosso padding per evitare duplicazione con i componenti interni
    maxWidth: 'calc(100% - 240px)',
  },

  // Tablet medio: sidebar più stretta.
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    padding: 0, // Rimosso padding per evitare duplicazione con i componenti interni
    maxWidth: 'calc(100% - 220px)',
  },

  // Mobile: nessuna sidebar fissa, drawer overlay
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    padding: 0, // Rimosso padding per evitare duplicazione con i componenti interni
    marginTop: '60px', // Spazio per l'header mobile
    maxWidth: '100%', // Aggiunto maxWidth per prevenire overflow
  },

  // Mobile molto piccolo
  [theme.breakpoints.down('xs')]: {
    padding: 0, // Rimosso padding per evitare duplicazione con i componenti interni
    marginTop: '60px',
    maxWidth: '100%', // Aggiunto maxWidth per prevenire overflow
  },
}));

// Container interno completamente fluido e responsive
const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '16px', // Aggiunto padding generale al container
  boxSizing: 'border-box',
  overflow: 'visible',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative', // Aggiunto per gestire meglio il layout
}));

// Mutation per eliminare un report custom
const DELETE_CUSTOM_REPORT = gql`
  mutation deleteCustomReport($id: String!) {
    deleteCustomReport(id: $id)
  }
`;

const connectorKeys = [
  'shopify',
  'magento',
  'facebook',
  'googleAds',
  'googleAnalytics4',
  'kipify',
];
function normalizeRows(rawRows: any): Record<string, any[]> {
  const rows = typeof rawRows === 'object' && rawRows !== null ? rawRows : {};
  const normalized: Record<string, any[]> = {};
  connectorKeys.forEach((key) => {
    const value = rows[key];
    // Gestisce i casi: null, undefined, array vuoto, array con dati
    normalized[key] = Array.isArray(value) ? value : [];
  });
  return normalized;
}

// Funzione per convertire metricValuesRowsGroupedByConnector in formato tabella
function convertToTableRows(metricValuesRowsGroupedByConnector: any): any[][] {
  if (
    !metricValuesRowsGroupedByConnector ||
    typeof metricValuesRowsGroupedByConnector !== 'object'
  ) {
    return [[]];
  }

  // Converte la struttura MetricValuesRowsGroupedByConnector in formato tabella
  const tableRows: any[][] = [];

  Object.entries(metricValuesRowsGroupedByConnector).forEach(
    ([connectorType, metricValuesRows]) => {
      if (Array.isArray(metricValuesRows) && metricValuesRows.length > 0) {
        metricValuesRows.forEach((metricValuesRow) => {
          if (metricValuesRow && typeof metricValuesRow === 'object') {
            const row: any[] = [];

            // Aggiunge le split values
            if (Array.isArray(metricValuesRow.splitValues)) {
              metricValuesRow.splitValues.forEach((splitItem: any) => {
                if (
                  splitItem &&
                  splitItem.key &&
                  splitItem.value !== undefined
                ) {
                  row.push({
                    id: splitItem.key,
                    title: getTitle(splitItem.key), // Usa getTitle per convertire la chiave in nome leggibile
                    type: connectorType,
                    value: splitItem.value,
                    isSplit: true,
                  });
                }
              });
            }

            // Aggiunge le metric values
            if (Array.isArray(metricValuesRow.metricValues)) {
              metricValuesRow.metricValues.forEach((metricItem: any) => {
                if (
                  metricItem &&
                  metricItem.key &&
                  metricItem.value !== undefined
                ) {
                  row.push({
                    id: metricItem.key,
                    title: getTitle(metricItem.key), // Usa getTitle per convertire la chiave in nome leggibile
                    type: connectorType,
                    value: metricItem.value,
                    isSplit: false,
                  });
                }
              });
            }

            if (row.length > 0) {
              tableRows.push(row);
            }
          }
        });
      }
    },
  );

  return tableRows.length > 0 ? tableRows : [[]];
}

/**
 * CustomReport Page
 * - Two-column layout: fixed sidebar (width SIDEBAR_WIDTH) and content with consistent margin-left.
 * - Responsive report table: never below sidebar, horizontally scrollable on mobile.
 * - After each save/delete, the list is updated with automatic refetch.
 * - If the report config is invalid, shows only error message and hides save buttons.
 * - UX: no overflow, no "ghost" report after saving, immediate feedback.
 */
const CustomReport: React.FC = () => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  // Hook per ottenere i connettori configurati
  const {
    googleAdsConnectorId,
    shopifyConnectorId,
    facebookConnectorId,
    googleAnalytics4ConnectorId,
    clientAccountId,
    propertyId,
  } = useBackendConnectors();

  // Query per ottenere i report con cache policy ottimizzata
  const { data, loading, refetch } = useQuery(GET_CUSTOM_REPORTS, {
    fetchPolicy: 'cache-first', // Usa cache se disponibile, altrimenti network
    nextFetchPolicy: 'cache-first', // Mantieni cache-first per richieste successive
  });

  // Mutations
  const [createCustomReport] = useMutation(CREATE_CUSTOM_REPORT);
  const [updateCustomReport] = useMutation(UPDATE_CUSTOM_REPORT);
  const [deleteCustomReport] = useMutation(DELETE_CUSTOM_REPORT, {
    refetchQueries: ['getCustomReports'],
  });

  // Persistenza della selezione del report
  const [selectedReport, setSelectedReport] = useState<CustomReportType | null>(
    null,
  );
  const [selectedReportConfig, setSelectedReportConfig] = useState<any>(null);
  const [savingReport, setSavingReport] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [originalReportTitle, setOriginalReportTitle] = useState(''); // Titolo originale dal database
  const [isDirty, setIsDirty] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Gestione URL per persistenza del report selezionato
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const reportId = searchParams.get('id');

    // Se c'è un ID nell'URL e i dati sono caricati, seleziona il report
    if (reportId && data?.getCustomReports && !selectedReport) {
      const report = data.getCustomReports.find(
        (r: CustomReportType) => r.id === reportId,
      );
      if (report) {
        setSelectedReport(report);
        setSelectedReportConfig(null);
        setOriginalReportTitle(report.name);
        setIsDirty(false);
        // Non aggiornare l'URL qui perché è già nell'URL
      }
    }
  }, [location.search, data, selectedReport]);

  // Hook sempre dichiarati in cima
  const [metricsIsShown] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  // Filtri comparativi (come in KPI)
  const [compareMode, setCompareMode] = useState<string>('none'); // 'none' | 'previous_period' | 'previous_year' | 'custom'
  const [compareDateRange, setCompareDateRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [showCustomCompareDates, setShowCustomCompareDates] = useState(false);
  const [tempCustomCompareDates, setTempCustomCompareDates] = useState<{
    from: string;
    to: string;
  } | null>(null);

  // Modal di conferma uscita (sempre attivo, senza logica dirty)
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitAction, setExitAction] = useState<null | (() => void)>(null);

  // Feedback visivo salvataggio titolo
  const [showTitleSaved, setShowTitleSaved] = useState(false);
  const [titleSavedTimer, setTitleSavedTimer] = useState<NodeJS.Timeout | null>(
    null,
  );

  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { lookbackDays } = useTrialStatus();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Quando la sidebar è collapsed (72px), il contenuto si allarga come nel tool Inventory
  const containerSxWhenCollapsed = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const rows = useMemo(() => {
    if (selectedReport?.config?.metricValuesRowsGroupedByConnector) {
      return convertToTableRows(
        selectedReport.config.metricValuesRowsGroupedByConnector,
      );
    }
    return [[]];
  }, [selectedReport]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  // Reset isDirty when report is updated remotely
  useEffect(() => {
    if (selectedReport && data?.getCustomReports) {
      const updatedReport = data.getCustomReports.find(
        (r: any) => r.id === selectedReport.id,
      );
      if (
        updatedReport &&
        JSON.stringify(updatedReport) !== JSON.stringify(selectedReport)
      ) {
        // Update report only if we're not saving and there are no pending changes
        if (!savingReport && !isDirty) {
          setSelectedReport(updatedReport);
          setSelectedReportConfig(null);
        }
      }
    }
  }, [data, selectedReport, savingReport, isDirty]);

  useEffect(() => {
    if (selectedReport) {
      setReportTitle(selectedReport.name);
      // Update original title only when selecting a new report or when it's saved
      if (originalReportTitle === '' || selectedReport.name !== reportTitle) {
        setOriginalReportTitle(selectedReport.name);
      }
      // Inizializza columnOrder dal config salvato
      if (
        selectedReport.config?.columnOrder &&
        Array.isArray(selectedReport.config.columnOrder) &&
        selectedReport.config.columnOrder.length > 0
      ) {
        setColumnOrder(selectedReport.config.columnOrder);
      }
    }
  }, [selectedReport, originalReportTitle, reportTitle]);

  // Attiva isDirty su modifica titolo
  const handleTitleChange = (newTitle: string) => {
    setReportTitle(newTitle);
    if (selectedReport) {
      setSelectedReport({ ...selectedReport, name: newTitle });
      // Solo se il titolo è effettivamente diverso da quello originale
      if (newTitle.trim() !== originalReportTitle && newTitle.trim() !== '') {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
  };

  // Attiva isDirty su modifica config
  const handleConfigChange = (config: any) => {
    // Filtra kipify prima di salvare la config
    const filteredConfig = filterValidGraphQLConnectorsFromConfig(config);
    setSelectedReportConfig(filteredConfig);
    // Solo se la config è effettivamente diversa da quella salvata
    if (
      JSON.stringify(filteredConfig) !== JSON.stringify(selectedReport?.config)
    ) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  };

  // Feedback visivo per salvataggio titolo
  const showTitleSavedFeedback = () => {
    setShowTitleSaved(true);
    if (titleSavedTimer) {
      clearTimeout(titleSavedTimer);
    }
    const timer = setTimeout(() => {
      setShowTitleSaved(false);
    }, 2000);
    setTitleSavedTimer(timer);
  };

  // Navigation guard per beforeunload (solo se isDirty)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue =
          'Are you sure you want to exit? Your changes might be lost.';
        return e.returnValue;
      }
    };
    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () =>
        window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [isDirty]);

  // Intercettazione navigazione dalla sidebar e altri link
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      // Solo se siamo in modalità editing e ci sono modifiche non salvate
      if (link && isDirty && selectedReport) {
        const href = link.getAttribute('href');
        // Verifica che sia un link di navigazione (non un link interno alla pagina)
        if (
          href &&
          href !== location.pathname &&
          href !== '#' &&
          !href.startsWith('javascript:') &&
          !href.startsWith('mailto:') &&
          !href.startsWith('tel:')
        ) {
          // Previeni la navigazione solo se ci sono modifiche non salvate
          e.preventDefault();
          e.stopPropagation();

          setExitAction(() => () => {
            navigate(href);
          });
          setShowExitDialog(true);
        }
      }
    };

    // Usa capture: false per evitare conflitti con altri event listener
    document.addEventListener('click', handleClick, false);
    return () => document.removeEventListener('click', handleClick, false);
  }, [isDirty, navigate, location.pathname, selectedReport]);

  // Funzione di reset manuale per stati bloccati (debug)
  const resetDirtyState = () => {
    setIsDirty(false);
    setSelectedReportConfig(null);
  };

  // Gestione navigazione con modal di conferma (solo se isDirty)
  const handleNavigation = (navigationFn: () => void) => {
    if (isDirty) {
      setExitAction(() => navigationFn);
      setShowExitDialog(true);
    } else {
      navigationFn();
    }
  };

  const handleSaveAndExit = async () => {
    if (selectedReport) {
      const configToSave = selectedReportConfig || selectedReport.config;
      if (configToSave) {
        // Assicurati che columnOrder sia incluso nel config salvato
        const configWithColumnOrder = {
          ...configToSave,
          columnOrder:
            columnOrder.length > 0
              ? columnOrder
              : configToSave.columnOrder || selectedReport.config?.columnOrder,
        };
        await handleSaveReport(configWithColumnOrder);
      }
    }
    setShowExitDialog(false);
    if (exitAction) exitAction();
  };

  const handleExitWithoutSave = () => {
    setShowExitDialog(false);
    if (exitAction) exitAction();
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
    setExitAction(null);
  };

  // Gestione selezione report
  const handleSelectReport = (report: CustomReportType) => {
    setSelectedReport(report);
    setSelectedReportConfig(null);
    setOriginalReportTitle(report.name); // Reset del titolo originale con quello del report selezionato
    setIsDirty(false); // Reset isDirty quando si seleziona un nuovo report

    // Aggiorna l'URL con l'ID del report per persistenza al ricaricamento
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('id', report.id);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Gestione salvataggio
  const handleSaveReport = async (config: any) => {
    if (!selectedReport) return;
    setSavingReport(true);
    try {
      // Filtra kipify prima di salvare
      const filteredConfig = filterValidGraphQLConnectorsFromConfig(config);

      if (!selectedReport.id) {
        // CREATE
        const { data } = await createCustomReport({
          variables: {
            input: {
              name: reportTitle.trim() || 'New report',
              config: filteredConfig,
            },
          },
        });
        if (data?.createCustomReport) {
          setSelectedReport(data.createCustomReport);
          setReportTitle(data.createCustomReport.name); // Update title with saved one
          snackbar('Report created successfully', 'success');
          refetch();
        }
      } else {
        // UPDATE
        const { data } = await updateCustomReport({
          variables: {
            id: selectedReport.id,
            input: {
              name: reportTitle.trim() || selectedReport.name,
              config: filteredConfig,
            },
          },
        });
        if (data?.updateCustomReport) {
          setSelectedReport(data.updateCustomReport);
          setReportTitle(data.updateCustomReport.name); // Update title with saved one
        }
        snackbar('Saved successfully', 'success');
        refetch();
      }
      // Reset isDirty solo dopo salvataggio riuscito
      setIsDirty(false);
      setSelectedReportConfig(null); // Reset config per evitare conflitti
    } catch (error) {
      console.error('Error saving custom report:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      snackbar(`Error saving changes: ${errorMessage}`, 'error');
    } finally {
      setSavingReport(false);
    }
  };

  // Salvataggio titolo con feedback visivo
  const handleSaveTitle = async (newTitle: string) => {
    if (!selectedReport || !selectedReport.id) {
      return;
    }

    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      return; // Non salvare titoli vuoti
    }

    // Evita salvataggi non necessari solo se il titolo è effettivamente uguale all'originale
    if (trimmedTitle === originalReportTitle) {
      return;
    }

    try {
      // Filtra kipify anche quando si salva solo il titolo
      // Assicurati che columnOrder sia incluso nel config salvato
      const configWithColumnOrder = {
        ...selectedReport.config,
        columnOrder:
          columnOrder.length > 0
            ? columnOrder
            : selectedReport.config?.columnOrder,
      };
      const filteredConfig = filterValidGraphQLConnectorsFromConfig(
        configWithColumnOrder,
      );
      const { data } = await updateCustomReport({
        variables: {
          id: selectedReport.id,
          input: {
            name: trimmedTitle,
            config: filteredConfig,
          },
        },
      });

      if (data?.updateCustomReport) {
        setSelectedReport(data.updateCustomReport);
        setReportTitle(data.updateCustomReport.name);
        // Update original title only after successful save
        setOriginalReportTitle(data.updateCustomReport.name);
      }

      // Reset isDirty solo se non ci sono altre modifiche pendenti
      if (!selectedReportConfig) {
        setIsDirty(false);
      }
      showTitleSavedFeedback();
      refetch();
    } catch (error) {
      // TODO: gestire logging in modo appropriato in produzione
      snackbar('Error saving title', 'error');
    }
  };

  // Funzione sicura per il parsing delle date
  const safeDateParse = (dateStr: string) => {
    return formatDateForDisplay(dateStr);
  };

  // Normalizzazione della configurazione (spostata prima per essere usata nei callback)
  const normalizedConfig = useMemo(() => {
    if (!selectedReport) return null;
    return {
      ...selectedReport.config,
      dateRange: selectedReport.config?.dateRange || { from: '', to: '' },
      rows: normalizeRows(
        selectedReport.config?.rows ||
          selectedReport.config?.metricValuesRowsGroupedByConnector ||
          {},
      ),
      columnOrder:
        columnOrder.length > 0
          ? columnOrder
          : selectedReport.config?.columnOrder,
    };
  }, [selectedReport, columnOrder]);

  // Date range "effettivo" usato dal report in tempo reale:
  // - se l'utente sta modificando (selectedReportConfig), usa quello
  // - altrimenti usa la config salvata (normalizedConfig)
  // Questo è CRITICO per il confronto: altrimenti "Previous period/year" viene calcolato
  // su un dateRange vecchio (es. il default "oggi") e il backend riceve un compareDateRange
  // di 1 giorno, come nei log Docker.
  const effectiveDateRange = useMemo(() => {
    const draft = selectedReportConfig?.dateRange;
    const saved = normalizedConfig?.dateRange;
    return draft?.from && draft?.to ? draft : saved;
  }, [selectedReportConfig?.dateRange, normalizedConfig?.dateRange]);

  const lookbackMinDate = useMemo(() => {
    if (lookbackDays === null || lookbackDays === undefined) return null;
    const min = new Date();
    min.setDate(min.getDate() - lookbackDays);
    return min.toISOString().split('T')[0];
  }, [lookbackDays]);

  const isRangeWithinLookback = useCallback(
    (range: { from: string; to: string } | null | undefined) => {
      if (!range || !lookbackMinDate) return true;
      return range.from >= lookbackMinDate;
    },
    [lookbackMinDate],
  );

  const previousPeriodRangeCandidate = useMemo(() => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;
    return getPreviousPeriodDateRange(effectiveDateRange);
  }, [effectiveDateRange]);
  const previousYearRangeCandidate = useMemo(() => {
    if (!effectiveDateRange?.from || !effectiveDateRange?.to) return null;
    return getPreviousYearDateRange(effectiveDateRange);
  }, [effectiveDateRange]);

  const canUsePreviousPeriodCompare = useMemo(
    () => isRangeWithinLookback(previousPeriodRangeCandidate),
    [isRangeWithinLookback, previousPeriodRangeCandidate],
  );
  const canUsePreviousYearCompare = useMemo(
    () => isRangeWithinLookback(previousYearRangeCandidate),
    [isRangeWithinLookback, previousYearRangeCandidate],
  );

  // Gestione compare mode (come in KPI)
  const handleCompareModeChange = useCallback(
    (mode: string) => {
      if (mode === 'previous_period' && !canUsePreviousPeriodCompare) {
        snackbar(
          getLookbackCompareBlockedMessage('previous_period', lookbackDays),
          'warning',
        );
        return;
      }
      if (mode === 'previous_year' && !canUsePreviousYearCompare) {
        snackbar(
          getLookbackCompareBlockedMessage('previous_year', lookbackDays),
          'warning',
        );
        return;
      }
      setCompareMode(mode);
      const currentDateRange = effectiveDateRange || { from: '', to: '' };

      if (mode === 'none') {
        setCompareDateRange(null);
        setShowCustomCompareDates(false);
        setTempCustomCompareDates(null);
      } else if (mode === 'previous_period') {
        if (currentDateRange.from && currentDateRange.to) {
          setCompareDateRange(getPreviousPeriodDateRange(currentDateRange));
        }
        setShowCustomCompareDates(false);
      } else if (mode === 'previous_year') {
        if (currentDateRange.from && currentDateRange.to) {
          setCompareDateRange(getPreviousYearDateRange(currentDateRange));
        }
        setShowCustomCompareDates(false);
      } else if (mode === 'custom') {
        setShowCustomCompareDates(true);
        setTempCustomCompareDates(compareDateRange || currentDateRange);
      }
    },
    [
      effectiveDateRange,
      compareDateRange,
      canUsePreviousPeriodCompare,
      canUsePreviousYearCompare,
      snackbar,
      lookbackDays,
    ],
  );

  // Aggiorna compare date range quando cambia il date range principale
  useEffect(() => {
    if (!effectiveDateRange) return;
    if (compareMode === 'previous_period') {
      setCompareDateRange(getPreviousPeriodDateRange(effectiveDateRange));
    } else if (compareMode === 'previous_year') {
      setCompareDateRange(getPreviousYearDateRange(effectiveDateRange));
    }
  }, [effectiveDateRange, compareMode]);

  useEffect(() => {
    if (compareMode === 'previous_period' && !canUsePreviousPeriodCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_period', lookbackDays),
        'warning',
      );
    } else if (compareMode === 'previous_year' && !canUsePreviousYearCompare) {
      setCompareMode('none');
      setCompareDateRange(null);
      snackbar(
        getLookbackCompareAutoDisabledMessage('previous_year', lookbackDays),
        'warning',
      );
    }
  }, [
    compareMode,
    canUsePreviousPeriodCompare,
    canUsePreviousYearCompare,
    lookbackDays,
    snackbar,
  ]);

  const handleApplyCustomCompareDates = useCallback(() => {
    if (tempCustomCompareDates?.from && tempCustomCompareDates?.to) {
      if (tempCustomCompareDates.from <= tempCustomCompareDates.to) {
        if (!isRangeWithinLookback(tempCustomCompareDates)) {
          snackbar(
            getLookbackCustomRangeBlockedMessage(lookbackDays, 'compare'),
            'warning',
          );
          return;
        }
        setCompareDateRange(tempCustomCompareDates);
        setShowCustomCompareDates(false);
        setTempCustomCompareDates(null);
      }
    }
  }, [tempCustomCompareDates, isRangeWithinLookback, snackbar, lookbackDays]);

  if (selectedReport && normalizedConfig) {
    const configValida = true;
    return (
      <>
        <CustomReportContainer sx={containerSxWhenCollapsed}>
          <ContentContainer>
            {/* Pulsanti migliorati con layout responsive ottimizzato */}
            <Box
              sx={{
                mt: { xs: 1, sm: 2, md: 3 },
                mb: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.5, sm: 2, md: 3 },
                px: { xs: 1, sm: 2, md: 3 }, // Allineato con il sistema di spacing
                py: { xs: 1.5, sm: 2 },
                borderBottom: `1px solid ${theme.palette.divider}`,
                width: '100%',
                minWidth: 0, // Importante per prevenire overflow
                maxWidth: '100%', // Aggiunto per prevenire overflow
                boxSizing: 'border-box', // Aggiunto per calcolo corretto delle dimensioni
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() =>
                  handleNavigation(() => {
                    setSelectedReport(null);
                    // Rimuovi l'ID dall'URL quando si chiude il report
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('id');
                    navigate(
                      `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
                      { replace: true },
                    );
                  })
                }
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                  minWidth: { xs: 120, sm: 140 },
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0, // Previene il restringimento del pulsante
                }}
              >
                BACK
              </Button>
              {configValida && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={savingReport}
                  onClick={async () => {
                    const configToSave =
                      selectedReportConfig || selectedReport.config;
                    if (configToSave) {
                      // Assicurati che columnOrder sia incluso nel config salvato
                      const configWithColumnOrder = {
                        ...configToSave,
                        columnOrder:
                          columnOrder.length > 0
                            ? columnOrder
                            : configToSave.columnOrder ||
                              selectedReport.config?.columnOrder,
                      };
                      await handleSaveReport(configWithColumnOrder);
                    } else {
                      alert('Configuration not available.');
                    }
                  }}
                  sx={{
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 700,
                    borderRadius: 2,
                    boxShadow: 3,
                    minWidth: { xs: 160, sm: 180 },
                    width: { xs: '100%', sm: 'auto' },
                    flexShrink: 0, // Previene il restringimento del pulsante
                    '&:hover': {
                      boxShadow: 6,
                    },
                  }}
                >
                  {savingReport ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1, color: 'white' }}
                      />
                      SAVING...
                    </>
                  ) : (
                    'SAVE CHANGES'
                  )}
                </Button>
              )}
            </Box>
            {/* Filtri comparativi (come in KPI) */}
            {configValida && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  mt: showCustomCompareDates ? 1.5 : 0,
                  mb: 2,
                  px: { xs: 1, sm: 2, md: 3 },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Compare:
                </Typography>
                <FormControl
                  size="small"
                  sx={{
                    width: { xs: '100%', sm: 160 },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      minHeight: '36px',
                    },
                  }}
                >
                  <Select
                    value={compareMode}
                    onChange={(e) => handleCompareModeChange(e.target.value)}
                    sx={{
                      backgroundColor: 'white',
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <MenuItem value="none" sx={{ fontSize: '0.875rem' }}>
                      None
                    </MenuItem>
                    <MenuItem
                      value="previous_period"
                      disabled={!canUsePreviousPeriodCompare}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Previous period
                    </MenuItem>
                    <MenuItem
                      value="previous_year"
                      disabled={!canUsePreviousYearCompare}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      Previous year
                    </MenuItem>
                    <MenuItem value="custom" sx={{ fontSize: '0.875rem' }}>
                      Custom range
                    </MenuItem>
                  </Select>
                </FormControl>

                {showCustomCompareDates && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <TextField
                      type="date"
                      label="From"
                      size="small"
                      value={
                        tempCustomCompareDates?.from ??
                        compareDateRange?.from ??
                        ''
                      }
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        const currentTo =
                          tempCustomCompareDates?.to ?? compareDateRange?.to;
                        if (currentTo && newFrom > currentTo) {
                          setTempCustomCompareDates({
                            from: newFrom,
                            to: newFrom,
                          });
                        } else {
                          setTempCustomCompareDates({
                            from: newFrom,
                            to:
                              tempCustomCompareDates?.to ??
                              compareDateRange?.to ??
                              '',
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: lookbackMinDate || undefined,
                        max:
                          (tempCustomCompareDates?.to ??
                            compareDateRange?.to) ||
                          undefined,
                      }}
                      sx={{
                        width: { xs: '100%', sm: 150 },
                        backgroundColor: 'white',
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          minHeight: '36px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <TextField
                      type="date"
                      label="To"
                      size="small"
                      value={
                        tempCustomCompareDates?.to ?? compareDateRange?.to ?? ''
                      }
                      onChange={(e) => {
                        const newTo = e.target.value;
                        const currentFrom =
                          tempCustomCompareDates?.from ??
                          compareDateRange?.from;
                        if (currentFrom && newTo < currentFrom) {
                          setTempCustomCompareDates({ from: newTo, to: newTo });
                        } else {
                          setTempCustomCompareDates({
                            from:
                              tempCustomCompareDates?.from ??
                              compareDateRange?.from ??
                              '',
                            to: newTo,
                          });
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min:
                          (tempCustomCompareDates?.from ??
                            compareDateRange?.from ??
                            lookbackMinDate) ||
                          lookbackMinDate ||
                          undefined,
                        max: new Date().toISOString().split('T')[0],
                      }}
                      sx={{
                        width: { xs: '100%', sm: 150 },
                        backgroundColor: 'white',
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                          minHeight: '36px',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      className={KIPIFY_FILTER_APPLY_BUTTON_CLASS}
                      onClick={handleApplyCustomCompareDates}
                      sx={{
                        minHeight: '36px',
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                )}
                {lookbackDays !== null && lookbackDays !== undefined && (
                  <Typography
                    variant="caption"
                    sx={{
                      width: '100%',
                      color: 'warning.main',
                      fontWeight: 500,
                      mt: 0.5,
                    }}
                  >
                    {getLookbackPlanLimitCaption(lookbackDays)}
                  </Typography>
                )}
              </Box>
            )}
            {configValida ? (
              <DashboardTable
                key={selectedReport.id}
                title={reportTitle}
                initData={normalizedConfig}
                onCloseTable={() =>
                  handleNavigation(() => {
                    setSelectedReport(null);
                    // Rimuovi l'ID dall'URL quando si chiude il report
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('id');
                    navigate(
                      `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
                      { replace: true },
                    );
                  })
                }
                onConfigChange={handleConfigChange}
                metricsIsShown={metricsIsShown}
                rows={(function () {
                  const tableRows = convertToTableRows(
                    selectedReport.config?.rows ||
                      selectedReport.config?.metricValuesRowsGroupedByConnector,
                  );
                  return tableRows;
                })()}
                columnOrder={columnOrder}
                changeColumnOrder={setColumnOrder}
                onTitleChange={handleTitleChange}
                titleInputRef={titleInputRef}
                onTitleSave={handleSaveTitle}
                showTitleSaved={showTitleSaved}
                hideSaveAndDeleteControls={true}
                // Pass connectors to allow loading metrics
                googleAdsConnectorId={googleAdsConnectorId}
                shopifyConnectorId={shopifyConnectorId}
                facebookConnectorId={facebookConnectorId}
                googleAnalytics4ConnectorId={googleAnalytics4ConnectorId}
                clientAccountId={clientAccountId}
                propertyId={propertyId}
                compareDateRange={
                  compareMode !== 'none' ? compareDateRange : null
                }
              />
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
                <Typography variant="h6" color="error">
                  Invalid or missing report configuration.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The selected report does not contain a valid configuration.
                  Try creating a new one or modifying it.
                </Typography>
              </Box>
            )}
          </ContentContainer>
        </CustomReportContainer>
        {/* Modal di conferma uscita migliorato */}
        <Dialog
          open={showExitDialog}
          onClose={handleCancelExit}
          aria-labelledby="exit-dialog-title"
          aria-describedby="exit-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: 8,
            },
          }}
        >
          <DialogTitle
            id="exit-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pb: 1,
              color: 'warning.main',
            }}
          >
            <WarningIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            <Typography variant="h6" component="span" fontWeight={600}>
              Confirm Exit
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText
              id="exit-dialog-description"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.6,
                color: 'text.primary',
                mb: 1,
              }}
            >
              Are you sure you want to exit the edit page?
            </DialogContentText>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You can save your changes before exiting or exit without saving.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
            <Button
              onClick={handleCancelExit}
              variant="outlined"
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExitWithoutSave}
              variant="outlined"
              color="error"
              sx={{
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                borderColor: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              Exit without saving
            </Button>
            <Button
              onClick={handleSaveAndExit}
              variant="contained"
              color="primary"
              autoFocus
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              Save and exit
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <CustomReportContainer sx={containerSxWhenCollapsed}>
      <ContentContainer>
        {/* Header ottimizzato per mobile e tablet */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: { xs: 1.5, sm: 2, md: 3 },
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            minWidth: 0, // Importante per prevenire overflow
            maxWidth: '100%', // Aggiunto per prevenire overflow
            boxSizing: 'border-box', // Aggiunto per calcolo corretto delle dimensioni
            px: { xs: 2, sm: 3, md: 4 }, // Aumentato padding orizzontale
            pt: { xs: 2, sm: 3, md: 4 }, // Aggiunto padding top per distacco dal bordo
            pb: { xs: 1, sm: 1.5, md: 2 }, // Aggiunto padding bottom
          }}
        >
          <Typography
            variant="h3"
            fontWeight={800}
            color="primary.main"
            sx={{
              mb: { xs: 0.5, sm: 0 },
              flex: 1,
              minWidth: 0,
              letterSpacing: 0.5,
              fontSize: {
                xs: '1.25rem',
                sm: '1.5rem',
                md: '1.75rem',
                lg: '2rem',
              },
              lineHeight: 1.2,
              wordBreak: 'break-word',
              hyphens: 'auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Custom Reports
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              minWidth: { xs: '100%', sm: 140, md: 160 },
              px: { xs: 1.5, sm: 2.5, md: 3 },
              py: { xs: 1, sm: 1, md: 1.2 },
              fontWeight: 700,
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              borderRadius: 2,
              boxShadow: 0,
              margin: 0,
              textTransform: 'none',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              wordBreak: { xs: 'break-word', sm: 'normal' },
              flexShrink: 0, // Previene il restringimento del pulsante
            }}
            onClick={() => {
              const now = new Date();
              const pad = (n: number) => n.toString().padStart(2, '0');
              const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
              setSelectedReport({
                id: '',
                name: 'New report',
                config: {
                  dateRange: { from: today, to: today },
                  metricValuesRowsGroupedByConnector: {},
                  rows: {},
                },
                createdAt: today,
                updatedAt: today,
              });
              setSelectedReportConfig(null);
              setIsDirty(false);
            }}
          >
            CREATE NEW REPORT
          </Button>
        </Box>

        {/* Container principale per la lista dei report */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2, md: 3 },
            width: '100%',
            flex: 1,
            overflow: 'visible',
            minWidth: 0, // Importante per prevenire overflow
            maxWidth: '100%', // Aggiunto per prevenire overflow
            boxSizing: 'border-box', // Aggiunto per calcolo corretto delle dimensioni
          }}
        >
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
              flex={1}
            >
              <CircularProgress color="primary" />
            </Box>
          )}

          {!loading && data?.getCustomReports?.length === 0 && (
            <Box
              textAlign="center"
              py={{ xs: 4, sm: 6, md: 8 }}
              flex={1}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                sx={{
                  width: { xs: 80, sm: 100, md: 120, lg: 140 },
                  height: { xs: 80, sm: 100, md: 120, lg: 140 },
                  mx: 'auto',
                  mb: { xs: 2, sm: 3 },
                  background: theme.palette.grey[200],
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.grey[500],
                  fontSize: { xs: 32, sm: 40, md: 48, lg: 56 },
                  boxShadow: 1,
                }}
              >
                📊
              </Box>
              <Typography
                variant="h5"
                color="text.primary"
                mb={1}
                fontWeight={700}
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}
              >
                No saved reports
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  maxWidth: { xs: 280, sm: 400, md: 500 },
                  mx: 'auto',
                }}
              >
                Save your custom reports to access them quickly in the future.
              </Typography>
            </Box>
          )}

          {!loading && data?.getCustomReports?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {/* Header della tabella - visibile su tablet e desktop */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: { xs: 1, md: 0 },
                  px: { xs: 1.5, sm: 2, md: 1.5, lg: 2 }, // Allineato con le righe della tabella
                  py: { md: 1, lg: 1.5 },
                  borderRadius: 2,
                  background: theme.palette.grey[100],
                  fontWeight: 700,
                  fontSize: { md: '0.8rem', lg: '0.875rem' },
                  letterSpacing: 0.2,
                  color: theme.palette.text.secondary,
                  boxShadow: 0,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    flex: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  Name
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 0.5, md: 0 },
                    minWidth: { xs: 'auto', md: 80, lg: 90 },
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  Created on
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 0.5, md: 0 },
                    minWidth: { xs: 'auto', md: 80, lg: 90 },
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  Last modified
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    display: 'flex',
                    justifyContent: { xs: 'stretch', md: 'flex-end' },
                    gap: { xs: 0.5, sm: 1 },
                    minWidth: 0,
                    flexShrink: 0,
                    textAlign: 'right',
                  }}
                >
                  Actions
                </Box>
              </Box>

              {/* Lista dei report con layout responsive ottimizzato */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1, sm: 1.5, md: 2 },
                  flex: 1,
                  overflow: 'visible',
                  width: '100%',
                  minWidth: 0, // Importante per prevenire overflow
                  maxWidth: '100%', // Aggiunto per prevenire overflow
                  boxSizing: 'border-box', // Aggiunto per calcolo corretto delle dimensioni
                }}
              >
                {data.getCustomReports.map((report: any) => {
                  const isEditing = editingId === report.id;
                  const titlePreview =
                    report.name.split(' ').slice(0, 3).join(' ') +
                    (report.name.split(' ').length > 3 ? '...' : '');
                  return (
                    <Box
                      key={report.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: { xs: 1, md: 0 },
                        px: { xs: 1.5, sm: 2, md: 1.5, lg: 2 },
                        py: { xs: 2, sm: 2.5, md: 1.5, lg: 2 },
                        borderRadius: 3,
                        background: 'white',
                        boxShadow: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'box-shadow 0.2s, border 0.2s',
                        width: '100%',
                        minWidth: 0, // Importante per prevenire overflow
                        maxWidth: '100%', // Aggiunto per prevenire overflow
                        boxSizing: 'border-box',
                        '&:hover': {
                          boxShadow: 4,
                          border: `1.5px solid ${theme.palette.primary.light}`,
                        },
                      }}
                    >
                      {/* Nome del report */}
                      <Box
                        sx={{
                          flex: 3,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {isMobile && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Name
                          </Typography>
                        )}
                        {isEditing ? (
                          <TextField
                            value={editingValue}
                            size="small"
                            variant="outlined"
                            autoFocus
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={async () => {
                              const trimmedValue = editingValue.trim();
                              if (
                                trimmedValue &&
                                trimmedValue !== report.name
                              ) {
                                try {
                                  const filteredConfig =
                                    filterValidGraphQLConnectorsFromConfig(
                                      report.config,
                                    );
                                  await updateCustomReport({
                                    variables: {
                                      id: report.id,
                                      input: {
                                        name: trimmedValue,
                                        config: filteredConfig,
                                      },
                                    },
                                  });
                                  refetch();
                                } catch (error) {
                                  // TODO: gestire logging in modo appropriato in produzione
                                }
                              }
                              setEditingId(null);
                              setEditingValue('');
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const trimmedValue = editingValue.trim();
                                if (
                                  trimmedValue &&
                                  trimmedValue !== report.name
                                ) {
                                  try {
                                    await updateCustomReport({
                                      variables: {
                                        id: report.id,
                                        input: {
                                          name: trimmedValue,
                                          config: report.config,
                                        },
                                      },
                                    });
                                    refetch();
                                  } catch (error) {
                                    // TODO: gestire logging in modo appropriato in produzione
                                  }
                                }
                                setEditingId(null);
                                setEditingValue('');
                              } else if (e.key === 'Escape') {
                                setEditingId(null);
                                setEditingValue('');
                              }
                            }}
                            inputProps={{ maxLength: 255 }}
                            sx={{
                              minWidth: { xs: 120, md: 150, lg: 180 },
                              fontWeight: 700,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              color="text.primary"
                              sx={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                maxWidth: { xs: '100%', md: 200, lg: 260 },
                                flex: 1,
                                minWidth: 0, // Importante per prevenire overflow
                              }}
                              title={report.name}
                            >
                              {titlePreview}
                            </Typography>
                            <IconButton
                              size="small"
                              sx={{ ml: 1, flexShrink: 0 }}
                              onClick={() => {
                                setEditingId(report.id);
                                setEditingValue(report.name);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      {/* Date con layout ottimizzato per mobile */}
                      <Box
                        sx={{
                          flex: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: { xs: 0.5, md: 0 },
                          minWidth: { xs: 'auto', md: 80, lg: 90 },
                          overflow: 'hidden',
                        }}
                      >
                        {isMobile ? (
                          <>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Created on
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {safeDateParse(report.createdAt)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Last modified
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {safeDateParse(report.updatedAt)}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1, textAlign: 'center' }}
                            >
                              {safeDateParse(report.createdAt)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: 1, textAlign: 'center' }}
                            >
                              {safeDateParse(report.updatedAt)}
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* Azioni con layout responsive */}
                      <Box
                        sx={{
                          flex: 2,
                          display: 'flex',
                          justifyContent: { xs: 'stretch', md: 'flex-end' },
                          gap: { xs: 0.5, sm: 1 },
                          mt: { xs: 1.5, md: 0 },
                          minWidth: 0, // Importante per prevenire overflow
                          flexShrink: 0, // Previene il restringimento dei bottoni
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={
                            <ArrowBackIcon
                              sx={{ transform: 'rotate(180deg)' }}
                            />
                          }
                          onClick={() => handleSelectReport(report)}
                          sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            px: { xs: 1, sm: 1.5, md: 2 },
                            py: { xs: 0.4, sm: 0.5 },
                            fontSize: {
                              xs: '0.8rem',
                              sm: '0.875rem',
                              md: '0.95rem',
                            },
                            boxShadow: 0,
                            transition: 'box-shadow 0.2s',
                            flex: { xs: 1, md: 'auto' },
                            '&:hover': {
                              boxShadow: 2,
                            },
                          }}
                        >
                          Open
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            if (
                              window.confirm(
                                'Are you sure you want to delete this report?',
                              )
                            ) {
                              deleteCustomReport({
                                variables: { id: report.id },
                              }).then(() => {
                                refetch();
                              });
                            }
                          }}
                          sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            px: { xs: 1, sm: 1.5, md: 2 },
                            py: { xs: 0.4, sm: 0.5 },
                            fontSize: {
                              xs: '0.8rem',
                              sm: '0.875rem',
                              md: '0.95rem',
                            },
                            boxShadow: 0,
                            transition: 'box-shadow 0.2s',
                            flex: { xs: 1, md: 'auto' },
                            '&:hover': {
                              boxShadow: 2,
                              background: theme.palette.error.light,
                            },
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </ContentContainer>
    </CustomReportContainer>
  );
};

export default CustomReport;
